import React, { useState, useEffect } from 'react';
import { fetchMenuItems } from '../../api/restaurantApi';
import { Box, Typography, Button, Grid, CardContent, IconButton, Tooltip } from '@mui/material';
const truncateWords = (text, limit = 20) => {
  const words = text.split(' ');
  return words.length > limit ? words.slice(0, limit).join(' ') + '...' : text;
};
import { Edit } from '@mui/icons-material';
import { MapPin, Phone, Clock3 } from 'lucide-react';
import MenuCard from '../ui/Card/MenuCard';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7071';

import RestaurantProfileShimmer from './RestaurantProfileShimmer';
import RestaurantFormModal from '../HotelInfo/RestaurantFormModal';

export default function RestaurantProfile({ restaurantData, loading }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const [menuItems, setMenuItems] = useState([]);
  const selectedDishes = menuItems.slice(0, 6);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const fetchEntries = () => {
    // Add logic to refresh restaurant data if needed
  };

  useEffect(() => {
    if (!restaurantData?._id || !restaurantData?.propertyId) return;
    fetchMenuItems(restaurantData._id, restaurantData.propertyId)
      .then(res => setMenuItems(res.data?.menu || []))
      .catch(err => console.error('❌ Failed to fetch menu items:', err));
  }, [restaurantData?._id, restaurantData?.propertyId]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (restaurantData?.photos?.length ? (prev + 1) % restaurantData.photos.length : 0));
    }, 4000);
    return () => clearInterval(interval);
  }, [restaurantData?.photos]);

  if (loading) {
    return <RestaurantProfileShimmer />;
  }

  return (
    <>
      <Box sx={{ px: 0 }}>
        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: 400, md: 480 },
            borderRadius: 3,
            overflow: 'hidden',
            mb: 6,
            pt:0,
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            {restaurantData?.photos?.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`Slide ${index + 1}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'brightness(0.7)',
                  opacity: index === currentSlide ? 1 : 0,
                  transition: 'opacity 1s ease-in-out',
                }}
              />
            ))}
          </Box>
          <IconButton
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              color: '#fff',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              }
            }}
            onClick={() => {
              setForm(restaurantData);
              setOpen(true);
            }}
          >
            <Edit />
          </IconButton>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              color: '#fff',
              px: { xs: 3, md: 8 },
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            {restaurantData?.name && (
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                Welcome to <span style={{ color: '#deaf59ff' }}>{restaurantData.name}</span>
              </Typography>
            )}
            {restaurantData?.subtitle && (
              <Typography variant="body1" sx={{ mb: 3 }}>
                {restaurantData.subtitle}
              </Typography>
            )}
            {Array.isArray(restaurantData?.openingHours) && (() => {
              const hours = restaurantData.openingHours;

              // Group days by the same schedule
              const groups = [];
              hours.forEach((entry, index) => {
                const timeRange = `${entry.open} - ${entry.close}`;
                if (
                  groups.length === 0 ||
                  groups[groups.length - 1].timeRange !== timeRange
                ) {
                  groups.push({ days: [index], timeRange });
                } else {
                  groups[groups.length - 1].days.push(index);
                }
              });

              const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

              return (
                <Box sx={{ mb: 2 }}>
                  {groups.map((group, idx) => {
                    const dayRange =
                      group.days.length === 1
                        ? dayNames[group.days[0]]
                        : `${dayNames[group.days[0]]} - ${dayNames[group.days[group.days.length - 1]]}`;
                    return (
                      <Typography key={idx} variant="body2" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                        <Clock3 size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        {dayRange}: {group.timeRange}
                      </Typography>
                    );
                  })}
                </Box>
              );
            })()}
            {(restaurantData?.tax || restaurantData?.serviceCharge) && (
              <Box sx={{ mb: 2 }}>
                {restaurantData?.tax?.enabled && (
                  <Typography variant="body2" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                    Tax: {restaurantData?.tax?.percentage || 0}%
                  </Typography>
                )}
                {restaurantData?.serviceCharge?.enabled && (
                  <Typography variant="body2" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
                    Service Charge: {restaurantData?.serviceCharge?.percentage || 0}%
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary">View Menu</Button>
              <Button variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }}>Booking</Button>
            </Box>
          </Box>
        </Box>

        {/* About Section */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: 720, mx: 'auto' }}>
            {restaurantData?.about && (
              <Typography variant="body1" gutterBottom sx={{ mb: 2, textAlign: 'center' }}>
                {restaurantData.about}
              </Typography>
            )}
            {restaurantData?.address && (
              <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Address: {restaurantData.address}
              </Typography>
            )}
            {restaurantData?.bookingContact && (
              <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                <Phone size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Contact: {restaurantData.bookingContact}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Featured Dishes / Special Dishes */}
        {selectedDishes.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Menu
            </Typography>
            <Box sx={{ display: 'flex', overflowX: 'auto', gap: 2, py: 1, px: 1 }}>
              {selectedDishes.map((item, index) => (
                <Box key={index} sx={{ minWidth: 240, flex: '0 0 auto' }}>
                  <MenuCard item={item}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {item?.name || '—'}
                        </Typography>
                        {item?.description?.split(' ').length > 8 ? (
                          <Tooltip title={item.description}>
                            <Typography variant="body2" color="text.secondary">
                              {truncateWords(item.description, 8)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {item.description || '—'}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                        {item?.weight && (
                          <Typography variant="caption" color="text.secondary">
                            {item.weight}
                          </Typography>
                        )}
                        {item?.price && (
                          <Typography variant="body1" fontWeight="bold">
                            ${item.price.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </MenuCard>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Map Embed */}
      {restaurantData?.mapEmbed && (
        <Box
          sx={{
            position: 'relative',
            width: '100vw',
            left: '50%',
            right: '50%',
            transform: 'translateX(-50%)',
            py: 3,
          }}
        >
          <Typography variant="h6" align="center" sx={{ mb: 2 }}>
            Find Us Here
          </Typography>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '7 / 2.5',
              overflow: 'hidden',
              '& iframe': {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100% !important',
                height: '100% !important',
                border: 0,
              },
            }}
            dangerouslySetInnerHTML={{ __html: restaurantData.mapEmbed }}
          />
          
        </Box>
        
      )}
      {restaurantData?.updatedAt && (
            <Typography variant="caption" align="center" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Last updated: {new Date(restaurantData.updatedAt).toLocaleString()}
            </Typography>
          )}
       <RestaurantFormModal
              open={open}
              onClose={() => setOpen(false)}
              initialData={form}
              onSubmit={async (data) => {
                const method = data._id ? 'PUT' : 'POST';
                const url = data._id
                  ? `${API_BASE}/api/restaurants/${data._id}`
                  : `${API_BASE}/api/restaurants`;
      
                const response = await fetch(url, {
                  method,
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(data)
                });
      
                if (response.ok) {
                  setOpen(false);
                  setForm({});
                  fetchEntries();
                } else {
                  console.error('Failed to save restaurant');
                }
              }}
            />
    </>
  );
}
